import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import LoadingButton from "./LoadingButton";
import { IconButton, useMediaQuery, useTheme } from "@mui/material";
import { Close } from "@mui/icons-material";

export interface DialogProps {
  title: string;
  okText?: string;
  cancelText?: string;
  element: React.ReactNode;
  onCancel?: () => Promise<void>;
  noCancel?: boolean;
  onOk: () => Promise<unknown>;
  open: boolean;
  okDisabled?: boolean;
  noActions?: boolean;
  maxWidth?: number;
  minWidth?: number;
  disableFullScreen?: boolean;
  noRightTopX?: boolean;
}

export type SetDialogProps = Pick<DialogProps, "okDisabled">;

let resolve: (value?: unknown) => void, reject: (reason?: any) => void;

export default function CommonDialog() {
  const [dialog, setDialog] = React.useState<DialogProps>({
    title: "",
    element: <i></i>,
    open: false,
    onOk: async () => {},
  });

  const [others, setDialogOthers] = React.useState<SetDialogProps>();

  const onCancel = async () => {
    if (!dialog.onCancel) {
      setDialog({ ...dialog, open: false, element: null });
      reject("canceled");
      return;
    }
    try {
      await dialog.onCancel();
      setDialog({ ...dialog, open: false, element: null });
      reject("canceled");
    } catch (error) {
      console.error(error);
      throw error;
      // todo display error message
    }
  };

  const onOk = async (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      var res = await dialog.onOk();
      setDialog({ ...dialog, open: false, element: null });
      resolve(res);
    } catch (error: any) {
      console.error(error);
      window.webApp.showMessage("error", `${error?.message || error}`);
      throw error;
    }
  };

  React.useEffect(() => {
    window.webApp = window.webApp || {};
    window.webApp.toggleDialog = (param) => {
      setDialog(param);
      setDialogOthers({ okDisabled: param.okDisabled });
      return new Promise((rs, rj) => {
        resolve = rs as any;
        reject = rj as any;
      });
    };
    window.webApp.setDialog = (param) => {
      setDialogOthers(param);
    };
  }, []);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Dialog
      sx={{
        zIndex: 1299,
        ".MuiDialog-container": {
          alignItems: dialog.disableFullScreen ? "flex-start" : "center",
        },
      }}
      fullScreen={dialog.disableFullScreen ? undefined : fullScreen}
      maxWidth={false}
      open={dialog.open}
    >
      <DialogTitle>
        {dialog.title}
        {!dialog.noRightTopX && (
          <IconButton
            aria-label="close"
            onClick={onCancel}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent
        sx={{
          minWidth: fullScreen ? "initial" : dialog.minWidth || 480,
          maxWidth: dialog.maxWidth,
        }}
      >
        {dialog.element &&
          React.cloneElement(dialog.element as any, { onClose: onCancel })}
      </DialogContent>
      {!dialog.noActions && (
        <DialogActions>
          {!dialog.noCancel && (
            <Button sx={{ mr: 4 }} onClick={onCancel}>
              {dialog.cancelText || "Cancel"}
            </Button>
          )}
          <LoadingButton disabled={others?.okDisabled} onClick={onOk}>
            {dialog.okText || "OK"}
          </LoadingButton>
        </DialogActions>
      )}
    </Dialog>
  );
}
